import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider, Helmet } from "react-helmet-async";
import App from './App';
import './index.css';
import 'react-quill/dist/quill.snow.css';
import 'leaflet/dist/leaflet.css';
import { Toaster } from './components/ui/toaster';
import { process, config } from './lib/env.js';
import { createPageUrl, createPageUrlWithParams } from './lib/routing.js';
import { uploadFile, getSignedUrl } from './lib/upload.js';
import { createAgent } from './lib/createAgent.js';
import User from './database/User.js';

// Initialize React Query client
export const queryClientInstance = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// --- Screenshot-ready signal ---
// Marks document.body[data-screenshot-ready] when window has loaded AND
// react-query has had zero in-flight queries for 600ms. Read by the
// screenshot Lambda (apps/serverless/src/screenshot-handler.ts) as the
// explicit "render done" marker, replacing fixed sleeps + reloads.
(function setupScreenshotReady() {
    let timer;
    let loaded = false;
    const markReady = () => {
        if (loaded) document.body.dataset.screenshotReady = "true";
    };
    const clearReady = () => {
        delete document.body.dataset.screenshotReady;
    };
    const checkIdle = () => {
        if (queryClientInstance.isFetching() === 0) {
            clearTimeout(timer);
            timer = setTimeout(markReady, 600);
        } else {
            clearTimeout(timer);
            clearReady();
        }
    };
    window.addEventListener("load", () => {
        loaded = true;
        requestAnimationFrame(() => requestAnimationFrame(checkIdle));
    });
    queryClientInstance.getQueryCache().subscribe(checkIdle);
})();

// Initialize globals for component access
window.React = React;
window.process = process;
window.config = config;
window.createPageUrl = createPageUrl;
window.createPageUrlWithParams = createPageUrlWithParams;
window.uploadFile = uploadFile;
window.getSignedUrl = getSignedUrl;
window.createAgent = createAgent;

// Initialize User authentication
window.User = User;

// Make integrations available globally
window.integrations = config.integrations || {};

// Make process, config, and integrations truly global for component code
// This allows components to use process.env.X, config.apiKey, and integrations directly
(function() {
    // For non-strict mode contexts
    try {
        self.process = process;
        self.config = config;
        self.integrations = window.integrations;
    } catch (e) {}
    
    // For module contexts - attach to globalThis
    globalThis.React = React;
    globalThis.process = process;
    globalThis.config = config;
    globalThis.integrations = window.integrations;
    
    // Ensure process.env is properly set
    if (!globalThis.process.env) {
        globalThis.process.env = {};
    }
    Object.assign(globalThis.process.env, process.env);
})();

// Configure axios with apiKey and environment variables
import axios from 'axios';

// Create axios instance with default configuration
const baseURL = process.env.PROXY_INTEGRATION_URL || 'https://api.agentui.ai/proxy';


const axiosInstance = axios.create({
    timeout: 80000, // 80 seconds default timeout
});

// Intercept legacy POST /upload with FormData and reroute to presigned S3 flow.
// Generated code sees the same response shape: {success, url, key, bucket, region}
// If the presigned flow fails BEFORE the S3 PUT, falls through to old /proxy/upload.
// If it fails AFTER S3 PUT (during upload-complete), retries upload-complete once
// before falling back, to avoid orphaned S3 objects.
function _installUploadRedirect(instance) {
    instance.interceptors.request.use(async (config) => {
        const url = config.url || '';
        const isUpload = config.method === 'post'
            && url.match(/\/upload(?:\?|$)/)
            && config.data instanceof FormData;

        if (!isUpload) return config;

        const formData = config.data;
        const file = formData.get('file');
        if (!file || !(file instanceof File || file instanceof Blob)) return config;

        try {
            const apiKey = window.config?.apiKey;
            const authToken = User.getToken ? User.getToken() : null;
            const proxyUrl = process.env.PROXY_INTEGRATION_URL || baseURL;
            const isPublic = url.includes('isPublic=true') || formData.get('isPublic') === 'true' || formData.get('isPublic') === true;
            const fileName = file.name || 'upload';
            const fileType = file.type || 'application/octet-stream';

            // Build headers for backend calls (api key + auth token if available)
            const backendHeaders = { 'x-api-key': apiKey };
            if (authToken) backendHeaders['Authorization'] = `Bearer ${authToken}`;

            // Step 1: Get presigned URL
            const presignRes = await fetch(
                `${proxyUrl}/upload-url?fileName=${encodeURIComponent(fileName)}&fileType=${encodeURIComponent(fileType)}&isPublic=${isPublic}`,
                { headers: backendHeaders }
            );
            if (!presignRes.ok) throw new Error('presign failed: ' + presignRes.status);
            const presignData = await presignRes.json();
            console.log('[uploadRedirect] presignData:', JSON.stringify(presignData));

            const presignedUrl = presignData.url;
            if (!presignedUrl) throw new Error('presign response missing url field: ' + JSON.stringify(Object.keys(presignData)));

            // Step 2: PUT directly to S3 (use fetch to avoid interceptors adding headers)
            const putRes = await fetch(presignedUrl, { method: 'PUT', body: file });
            if (!putRes.ok) throw new Error('S3 upload failed: ' + putRes.status);

            // Step 3: Register file record (retry once on failure to avoid orphaned S3 objects)
            const completeBody = JSON.stringify({
                key: presignData.key,
                fileName,
                fileType,
                fileSize: file.size,
                bucket: presignData.bucket,
                region: presignData.region,
                isPublic,
            });

            let completeRes = await fetch(`${proxyUrl}/upload-complete`, {
                method: 'POST',
                headers: { ...backendHeaders, 'Content-Type': 'application/json' },
                body: completeBody,
            });

            // Retry once — the file is already in S3, we must not orphan it
            if (!completeRes.ok) {
                console.warn('[uploadRedirect] upload-complete failed, retrying once...');
                completeRes = await fetch(`${proxyUrl}/upload-complete`, {
                    method: 'POST',
                    headers: { ...backendHeaders, 'Content-Type': 'application/json' },
                    body: completeBody,
                });
                if (!completeRes.ok) throw new Error('upload-complete failed after retry: ' + completeRes.status);
            }

            const completeData = await completeRes.json();

            // Return an adapter response that matches the old /proxy/upload shape
            config.adapter = () => Promise.resolve({
                data: {
                    success: true,
                    url: presignData.permanentUrl || presignData.key,
                    key: presignData.key,
                    bucket: presignData.bucket,
                    region: presignData.region,
                    fileId: completeData.fileId,
                },
                status: 200,
                statusText: 'OK',
                headers: { 'x-upload-redirected': 'presigned' },
                config,
                request: {},
            });

            return config;
        } catch (err) {
            // Presigned flow failed — fall through to old /proxy/upload endpoint
            console.warn('[uploadRedirect] Presigned upload failed, falling back to legacy upload:', err.message);
            return config;
        }
    });
}

// Lambda redirect routes — matches axios requests by URL pattern and reroutes to Lambda Function URLs.
// Each route: { match: string|RegExp to test against request URL, lambdaUrl: Lambda Function URL, method: HTTP method to match }
// Falls back to original backend URL if Lambda call fails.
const EXTRACT_DOCUMENT_LAMBDA_URL = process.env.EXTRACT_EXCEL_LAMBDA_URL || 'https://r57s2l2b6rhjix2ninisdgnjyu0tnena.lambda-url.us-east-2.on.aws/';

const LAMBDA_ROUTES = [
    {
        match: '/documents/extract-excel',
        lambdaUrl: EXTRACT_DOCUMENT_LAMBDA_URL,
        method: 'post',
    },
    {
        match: '/documents/extract-csv',
        lambdaUrl: EXTRACT_DOCUMENT_LAMBDA_URL,
        method: 'post',
    },
];

function _installLambdaRedirects(instance) {
    instance.interceptors.request.use(async (config) => {
        const url = config.url || '';
        const method = (config.method || 'get').toLowerCase();

        const route = LAMBDA_ROUTES.find((r) => {
            const methodMatch = r.method ? r.method === method : true;
            if (!methodMatch) return false;
            if (typeof r.match === 'string') return url.includes(r.match);
            return r.match.test(url);
        });

        if (!route) return config;

        try {
            const apiKey = window.config?.apiKey;
            if (!apiKey) throw new Error('No API key available');

            // Include the Bearer token (same as the regular axios auth
            // interceptor below). Without this, the Lambda only sees the
            // workflow API key and refuses APP_V2 + requiresLogin workflows
            // even for legitimately logged-in users.
            const authToken = (typeof User !== 'undefined' && User.getToken) ? User.getToken() : null;

            const headers = {
                'Content-Type': 'application/json',
                'x-api-key': apiKey,
            };
            if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

            const res = await fetch(route.lambdaUrl, {
                method: method.toUpperCase(),
                headers,
                body: config.data ? JSON.stringify(config.data) : undefined,
            });

            if (!res.ok) throw new Error(`Lambda ${route.match} failed: ${res.status}`);

            const data = await res.json();

            config.adapter = () => Promise.resolve({
                data,
                status: 200,
                statusText: 'OK',
                headers: { 'x-lambda-redirected': typeof route.match === 'string' ? route.match : 'regex' },
                config,
                request: {},
            });

            return config;
        } catch (err) {
            console.warn(`[lambdaRedirect] ${route.match} failed, falling back to backend:`, err.message);
            return config;
        }
    });
}

// Add request interceptor to include apiKey header
axiosInstance.interceptors.request.use((config) => {
    if (window.config?.apiKey) {
        config.headers = config.headers || {};
        config.headers['x-api-key'] = window.config.apiKey;
    }

    if (User.getToken()) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${User.getToken()}`;
    }
    return config;
});

_installUploadRedirect(axiosInstance);
_installLambdaRedirects(axiosInstance);

// Add response interceptor to handle errors with detailed logging and log successful requests
axiosInstance.interceptors.response.use(
    (response) => {
        // Log successful axios responses to parent window
        if (window.parent && window.parent !== window) {
            try {
                const responseLog = {
                    type: 'AXIOS_SUCCESS',
                    timestamp: Date.now(),
                    source: 'component-iframe',
                    request: {
                        url: response.config?.url,
                        method: response.config?.method?.toUpperCase(),
                    },
                    response: {
                        status: response.status,
                        statusText: response.statusText,
                        data: response.data,
                    }
                };

                window.parent.postMessage(responseLog, '*');
            } catch (e) {
                console.error('Failed to report axios success to parent:', e);
            }
        }

        return response;
    },
    (error) => {
        // Log failed axios requests to parent window for AI debugging
        if (window.parent && window.parent !== window) {
            try {
                const errorLog = {
                    type: 'AXIOS_ERROR',
                    timestamp: Date.now(),
                    source: 'component-iframe',
                    request: {
                        url: error.config?.url,
                        method: error.config?.method?.toUpperCase(),
                    },
                    error: {
                        message: error.message,
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                        data: error.response?.data,
                    }
                };

                window.parent.postMessage(errorLog, '*');
            } catch (e) {
                // Silently fail
            }
        }

        // Report to backend telemetry
        const status = error.response?.status;
        if (status >= 500 || !error.response) {
            const errMsg = status
                ? `HTTP error ${status} from ${error.config?.url || "unknown"}`
                : `${error.message} — axios ${error.config?.url || "unknown"}`;
            reportErrorToBackend(errMsg, "UNHANDLED_REJECTION", {
                apiUrl: error.config?.url,
                httpStatus: status,
                httpMethod: error.config?.method?.toUpperCase() || "GET",
                fetchError: error.code,
            });
            try { error.__telemetryReported = true; } catch (_) {}
        }

        return Promise.reject(error);
    }
);

// Make axios instance available globally
window.axios = axiosInstance;

// Also make process.env available for backward compatibility
window.process.env = process.env;


// Override the default axios to use our configured instance
// This ensures all axios calls use our configuration
const originalAxios = axios;
window.axios = axiosInstance;


axios.defaults.timeout = 80000;

// Add global interceptor to default axios as well
axios.interceptors.request.use((config) => {
    if (window.config?.apiKey) {
        config.headers = config.headers || {};
        config.headers['x-api-key'] = window.config.apiKey;
    }

    if (User.getToken()) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${User.getToken()}`;
    }
    return config;
});

_installUploadRedirect(axios);
_installLambdaRedirects(axios);

// Add response error interceptor to default axios as well
axios.interceptors.response.use(
    (response) => {
        // Log successful axios responses to parent window
        if (window.parent && window.parent !== window) {
            try {
                const responseLog = {
                    type: 'AXIOS_SUCCESS',
                    timestamp: Date.now(),
                    source: 'component-iframe',
                    request: {
                        url: response.config?.url,
                        method: response.config?.method?.toUpperCase(),
                    },
                    response: {
                        status: response.status,
                        statusText: response.statusText,
                        data: response.data,
                    }
                };

                window.parent.postMessage(responseLog, '*');
            } catch (e) {
                console.error('Failed to report axios success to parent:', e);
            }
        }

        return response;
    },
    (error) => {
        // Log failed axios requests to parent window for AI debugging
        if (window.parent && window.parent !== window) {
            try {
                const errorLog = {
                    type: 'AXIOS_ERROR',
                    timestamp: Date.now(),
                    source: 'component-iframe',
                    request: {
                        url: error.config?.url,
                        method: error.config?.method?.toUpperCase(),
                    },
                    error: {
                        message: error.message,
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                        data: error.response?.data,
                    }
                };

                window.parent.postMessage(errorLog, '*');
            } catch (e) {
                // Silently fail
            }
        }

        // Report to backend telemetry
        const status = error.response?.status;
        if (status >= 500 || !error.response) {
            const errMsg = status
                ? `HTTP error ${status} from ${error.config?.url || "unknown"}`
                : `${error.message} — axios ${error.config?.url || "unknown"}`;
            reportErrorToBackend(errMsg, "UNHANDLED_REJECTION", {
                apiUrl: error.config?.url,
                httpStatus: status,
                httpMethod: error.config?.method?.toUpperCase() || "GET",
                fetchError: error.code,
            });
            try { error.__telemetryReported = true; } catch (_) {}
        }

        return Promise.reject(error);
    }
);

console.log('Default axios baseURL set to:', axios.defaults.baseURL);

// Error reporting to parent window via postMessage
function reportErrorToParent(errorMessage, errorType = "ERROR") {
    if (window.parent && window.parent !== window) {
        try {
            window.parent.postMessage(
                {
                    type: errorType,
                    message: errorMessage,
                    timestamp: Date.now(),
                    source: "component-iframe",
                },
                "*"
            );
        } catch (e) {
            console.error("Failed to report error to parent:", e);
        }
    }
}

// Report current page/route to parent window for AI context awareness
function reportCurrentPageToParent() {
    if (window.parent && window.parent !== window) {
        try {
            const pathname = window.location.pathname;
            // Extract the page name from pathname (e.g., "/dashboard" -> "Dashboard", "/apps/xxx/settings" -> "Settings")
            const pathParts = pathname.split('/').filter(Boolean);
            let currentPage = pathParts[pathParts.length - 1] || 'home';
            
            // Handle /apps/{stepId}/ prefix - get the part after it
            if (pathParts[0] === 'apps' && pathParts.length > 2) {
                currentPage = pathParts[2] || 'home';
            } else if (pathParts[0] === 'apps' && pathParts.length === 2) {
                currentPage = 'home';
            }
            
            // Capitalize first letter for display
            const pageName = currentPage.charAt(0).toUpperCase() + currentPage.slice(1);
            
            window.parent.postMessage(
                {
                    type: "ROUTE_CHANGE",
                    route: pathname,
                    pageName: pageName,
                    timestamp: Date.now(),
                    source: "component-iframe",
                },
                "*"
            );
        } catch (e) {
            console.error("Failed to report route to parent:", e);
        }
    }
}

// Track route changes using history API
const originalPushState = history.pushState;
const originalReplaceState = history.replaceState;

history.pushState = function(...args) {
    originalPushState.apply(this, args);
    reportCurrentPageToParent();
};

history.replaceState = function(...args) {
    originalReplaceState.apply(this, args);
    reportCurrentPageToParent();
};

// Also listen for popstate (back/forward navigation)
window.addEventListener('popstate', () => {
    reportCurrentPageToParent();
});

// Report initial page on load
window.addEventListener('load', () => {
    // Small delay to ensure React Router has set up
    setTimeout(reportCurrentPageToParent, 100);
});

// --- Telemetry: report errors to backend for operational intelligence ---
const _errorReportCache = new Map();

function shouldReport(errorMessage) {
    const key = (errorMessage || "").slice(0, 100);
    const now = Date.now();
    const entry = _errorReportCache.get(key);
    if (!entry || now - entry.windowStart > 60000) {
        _errorReportCache.set(key, { count: 1, windowStart: now });
        return true;
    }
    entry.count++;
    return false;
}

function reportErrorToBackend(errorMessage, errorType = "ERROR", metadata = {}) {
    const workflowId = window.config?.workflowId;
    const stepId = window.config?.stepId;
    if (!workflowId) return;
    if (!shouldReport(errorMessage)) return;
    const telemetryUrl = window.process?.env?.TELEMETRY_URL;
    if (!telemetryUrl) return;
    // Send workflow API key + signed-in user token so the backend resolves
    // company + workflowUserId and dual-writes the partitioned client_errors
    // stream (brings runtime errors into the per-user Errors panel).
    const headers = { "Content-Type": "application/json" };
    if (window.config?.apiKey) headers["x-api-key"] = window.config.apiKey;
    try {
        const token = localStorage.getItem(`workflow-auth-${workflowId}`);
        if (token) headers["Authorization"] = `Bearer ${token}`;
    } catch {}
    fetch(`${telemetryUrl}/runtime-error`, {
        method: "POST",
        headers,
        body: JSON.stringify({
            workflowId, stepId,
            companyId: window.config?.companyId,
            errorType,
            errorMessage: (errorMessage || "").slice(0, 2000),
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: Date.now(),
            componentName: metadata?.componentName,
            stack: metadata?.stack,
            componentStack: metadata?.componentStack,
            metadata,
        }),
        keepalive: true,
    }).catch(() => {});
}
window.reportErrorToBackend = reportErrorToBackend;

// Ship caught React errors to logs.agentui.ai (Athena-backed). Independent of
// reportErrorToBackend above: that one feeds the DenoManager runtime-error
// telemetry stream; this one feeds the per-workflow-user Errors panel in the
// admin app. Both can fire from the same ErrorBoundary — losing one shouldn't
// silence the other.
function reportErrorToLogsAgentui(payload) {
    try {
        const workflowId = window.config?.workflowId;
        const apiKey = window.config?.apiKey;
        if (!workflowId || !apiKey) return;
        if (!shouldReport(payload?.errorMessage || "")) return;
        const endpoint =
            window.config?.errorsLogsEndpoint ||
            (window.config?.auditLogsEndpoint || "https://logs.agentui.ai") +
                `/workflows/${workflowId}/errors`;
        const headers = {
            "content-type": "application/json",
            "x-api-key": apiKey,
        };
        try {
            const token = localStorage.getItem(`workflow-auth-${workflowId}`);
            if (token) headers["authorization"] = `Bearer ${token}`;
        } catch {}
        fetch(endpoint, {
            method: "POST",
            headers,
            body: JSON.stringify({
                events: [
                    {
                        ts: Date.now(),
                        errorMessage: (payload?.errorMessage || "").slice(0, 500),
                        errorName: (payload?.errorName || "").slice(0, 500),
                        componentName: (payload?.componentName || "").slice(0, 500),
                        stack: (payload?.stack || "").slice(0, 16000),
                        componentStack: (payload?.componentStack || "").slice(0, 8000),
                        pageUrl: (window.location?.href || "").slice(0, 500),
                        metadata: payload?.metadata,
                    },
                ],
            }),
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* fire-and-forget */
    }
}
window.reportErrorToLogsAgentui = reportErrorToLogsAgentui;

// Console interception is handled in index.html before any modules load
// This ensures we capture logs from ALL code including imported modules

// Intercept fetch to inject auth headers and capture API failures
const _originalFetch = window.fetch;
window.fetch = function (input, init) {
    const url = typeof input === "string" ? input : input?.url || "";
    // Skip telemetry URLs to avoid recursive self-reporting loops
    const telemetryBase = window.process?.env?.TELEMETRY_URL;
    if (telemetryBase && url.startsWith(telemetryBase)) return _originalFetch.apply(this, arguments);

    // Auto-inject x-api-key and authorization headers for proxy/API requests
    const proxyBase = window.process?.env?.PROXY_INTEGRATION_URL || "";
    const apiBase = window.process?.env?.DATABASE_PROXY || "";
    if ((proxyBase && url.startsWith(proxyBase)) || (apiBase && url.startsWith(apiBase)) || url.includes("/proxy/") || url.includes("/databases/")) {
        init = init || {};
        const headers = new Headers(init.headers || {});
        if (!headers.has("x-api-key") && window.config?.apiKey) {
            headers.set("x-api-key", window.config.apiKey);
        }
        if (!headers.has("authorization") && typeof User !== "undefined" && User.getToken && User.getToken()) {
            headers.set("authorization", `Bearer ${User.getToken()}`);
        }
        init.headers = headers;
    }

    return _originalFetch.call(this, input, init).then((response) => {
        if (!response.ok && response.status >= 500) {
            const errMsg = `HTTP error ${response.status} from ${response.url || url}`;
            reportErrorToBackend(errMsg, "UNHANDLED_REJECTION", {
                apiUrl: response.url || url,
                httpStatus: response.status,
                httpMethod: init?.method || "GET",
            });
        }
        return response;
    }).catch((err) => {
        const errMsg = `${err.message} — fetching ${url}`;
        reportErrorToBackend(errMsg, "UNHANDLED_REJECTION", {
            apiUrl: url,
            httpMethod: init?.method || "GET",
            fetchError: err.name,
        });
        try { err.__telemetryReported = true; } catch (_) {}
        throw err;
    });
};

// Intercept XMLHttpRequest to capture axios/XHR API failures
const _XHROpen = XMLHttpRequest.prototype.open;
const _XHRSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.open = function (method, url) {
    this._telemetryMethod = method;
    this._telemetryUrl = url;
    return _XHROpen.apply(this, arguments);
};
XMLHttpRequest.prototype.send = function () {
    this.addEventListener("loadend", function () {
        const telemetryBase = window.process?.env?.TELEMETRY_URL;
        if (telemetryBase && this._telemetryUrl?.startsWith(telemetryBase)) return;
        if (this.status >= 500) {
            const errMsg = `HTTP error ${this.status} from ${this._telemetryUrl}`;
            reportErrorToBackend(errMsg, "UNHANDLED_REJECTION", {
                apiUrl: this._telemetryUrl,
                httpStatus: this.status,
                httpMethod: this._telemetryMethod || "GET",
            });
        }
    });
    return _XHRSend.apply(this, arguments);
};

// Global error handler
window.addEventListener("error", (event) => {
    const errorMessage = event.error
        ? `${event.error.message} at ${event.filename}:${event.lineno}:${event.colno}`
        : event.message;
    reportErrorToParent(errorMessage, "ERROR");
    reportErrorToBackend(errorMessage, "UNHANDLED_ERROR", {
        source: event.filename, lineno: event.lineno, colno: event.colno,
    });
});

// Unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    // Skip errors already reported by axios/fetch interceptors
    if (reason && reason.__telemetryReported) return;
    const errorMessage = reason
        ? reason.message || String(reason)
        : "Unhandled promise rejection";
    reportErrorToParent(errorMessage, "ERROR");
    reportErrorToBackend(errorMessage, "UNHANDLED_REJECTION", {
        stack: reason?.stack?.slice(0, 1000),
        name: reason?.name,
        status: reason?.status,
        url: reason?.url,
    });
});

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            componentName: null,
            functionName: null,
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("React Error Boundary caught an error:", error, errorInfo);
        
        const errorMessage = error.message || "Unknown error";
        const componentStack = errorInfo.componentStack || "";
        
        // Try to extract function/component name from error stack
        let functionName = "Unknown Function";
        if (error.stack) {
            // Look for function names in the stack trace, excluding ErrorBoundary
            const functionMatches = error.stack.match(/at\s+([A-Za-z][A-Za-z0-9_]*)\s*\(/g);
            if (functionMatches) {
                for (const match of functionMatches) {
                    const funcName = match.match(/at\s+([A-Za-z][A-Za-z0-9_]*)\s*\(/)[1];
                    if (funcName && funcName !== 'ErrorBoundary' && funcName !== 'getDerivedStateFromError') {
                        functionName = funcName;
                        break;
                    }
                }
            }
        }
        
        const lineMatch = error.stack?.match(/:(d+):\d+/);
        const lineInfo = lineMatch ? ` (line ${lineMatch[1]})` : "";
        
        // Extract component name from component stack if available
        let componentName = "Unknown Component";
        if (componentStack) {
            const componentMatch = componentStack.match(/in\s+([A-Za-z][A-Za-z0-9_]*)/);
            if (componentMatch) {
                componentName = componentMatch[1];
            }
        }

        // Prefer explicit boundary name if provided (route/layout wrappers pass this)
        const explicitName = this.props?.componentName;
        if (explicitName && typeof explicitName === "string") {
            componentName = explicitName;
        } else if (
            componentName === "Unknown Component" &&
            functionName &&
            functionName !== "Unknown Function"
        ) {
            // Fallback: show the first meaningful function name (often a component like Layout)
            componentName = functionName;
        }
        
        const detailedError = `Error in ${componentName} (${functionName}): ${errorMessage}${lineInfo}`;
        
        console.error(detailedError, {
            component: componentName,
            function: functionName,
            error: errorMessage,
            stack: error.stack,
            componentStack: componentStack
        });
        
        // Persist derived names so the in-iframe error UI shows real component names
        this.setState({ errorInfo, componentName, functionName });

        reportErrorToParent(detailedError, "ERROR");
        reportErrorToBackend(errorMessage, "REACT_ERROR", {
            componentStack: componentStack?.slice(0, 1000),
            componentName, functionName, lineInfo,
        });
        // Separate stream for the admin Errors panel — feeds logs.agentui.ai.
        // Fire-and-forget; never throws.
        try {
            window.reportErrorToLogsAgentui?.({
                errorMessage,
                errorName: error?.name,
                componentName,
                stack: error?.stack,
                componentStack,
                metadata: { functionName, lineInfo },
            });
        } catch {}
    }

    render() {
        if (this.state.hasError) {
            // Try to extract component and function names for display
            const displayName =
                this.state.componentName ||
                this.props.componentName ||
                "Unknown Component";
let functionName =
                this.state.functionName || "Unknown Function";
            
            if (this.state.error?.stack) {
                const functionMatches = this.state.error.stack.match(/at\s+([A-Za-z][A-Za-z0-9_]*)\s*\(/g);
                if (functionMatches) {
                    for (const match of functionMatches) {
                        const funcName = match.match(/at\s+([A-Za-z][A-Za-z0-9_]*)\s*\(/)[1];
                        if (funcName && funcName !== 'ErrorBoundary' && funcName !== 'getDerivedStateFromError') {
                            functionName = funcName;
                            break;
                        }
                    }
                }
            }
            
            return (
                <div style={{ 
                    padding: "2rem", 
                    background: "#fee", 
                    border: "1px solid #fcc", 
                    borderRadius: "8px", 
                    margin: "2rem",
                    fontFamily: "system-ui, -apple-system, sans-serif"
                }}>
                    <h3 style={{ color: "#c00", marginBottom: "0.5rem" }}>
                        ⚠️ Error in {displayName}
                    </h3>
                    <p style={{ color: "#800", marginBottom: "0.5rem" }}>
                        <strong>Function:</strong> {functionName}
                    </p>
                    <p style={{ color: "#800", marginBottom: "1rem" }}>
                        <strong>Error:</strong> {this.state.error?.message || "An error occurred while rendering this component"}
                    </p>
                    <details style={{ marginBottom: "1rem" }}>
                        <summary style={{ cursor: "pointer", color: "#666", marginBottom: "0.5rem" }}>
                            Show Error Details
                        </summary>
                        <pre style={{ 
                            fontSize: "0.75rem", 
                            color: "#666", 
                            whiteSpace: "pre-wrap",
                            background: "#f5f5f5",
                            padding: "0.5rem",
                            borderRadius: "4px",
                            overflow: "auto",
                            maxHeight: "200px"
                        }}>
                            {this.state.error?.stack || "No stack trace available"}
                        </pre>
                    </details>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: "0.5rem 1rem",
                            background: "#c00",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }}
                    >
                        Reload App
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClientInstance}>
      <ErrorBoundary componentName="App">
        <HelmetProvider>
          <App />
        </HelmetProvider>
        <Toaster />
      </ErrorBoundary>
    </QueryClientProvider>
  </React.StrictMode>
);
